import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { concatMap, map } from 'rxjs/operators';
import { UserPreferences } from '../models/user';
import { KeyBooleanPair } from '../models/base';

@Injectable({
  providedIn: 'root'
})
export class UserPreferencesService {
  constructor(
    private http: HttpClient,
    @Inject('env') private env: any,
    @Inject('errorInterceptorSkipHeader') private errorInterceptorSkipHeader: any
  ) {}

  // #region http methods
  public getUserPreferences(): Observable<UserPreferences> {
    const headers = new HttpHeaders().set(this.errorInterceptorSkipHeader, '');
    return this.http
      .get<string>(`${this.env.apiUrlRoot}/User/Preferences`, { headers })
      .pipe(map(prefs => (prefs ? JSON.parse(prefs) : prefs)));
  }

  public insertUserPreferences(preferences: string) {
    const headers = new HttpHeaders().set(this.errorInterceptorSkipHeader, '');
    return this.http
      .post(
        `${this.env.apiUrlRoot}/User/Preferences`,
        { Preferences: preferences },
        {
          headers
        }
      )
      .pipe(map(() => JSON.parse(preferences)));
  }

  public updateUserPreferences(preferences: string) {
    const headers = new HttpHeaders().set(this.errorInterceptorSkipHeader, '');
    return this.http
      .put(
        `${this.env.apiUrlRoot}/User/Preferences`,
        { Preferences: preferences },
        {
          headers
        }
      )
      .pipe(map(() => JSON.parse(preferences)));
  }

  // #end region

  // #region helper methods

  public setCompanyKey(clientCode: string, preferences: UserPreferences) {
    if (!preferences) preferences = new UserPreferences();

    if (!Object.keys(preferences.ClientPrefs).includes(clientCode))
      preferences.ClientPrefs[clientCode] = {
        Payroll: {
          SortEmployeeByLastName: null,
          ColumnVisibility: []
        }
      };

    return preferences;
  }

  public getPayrollEmployeeSortByLastName(clientCode: string, prefs: UserPreferences): boolean {
    if (!prefs?.ClientPrefs[clientCode] || !prefs?.ClientPrefs[clientCode]?.Payroll) {
      return null;
    }

    return prefs.ClientPrefs[clientCode].Payroll.SortEmployeeByLastName;
  }

  public getPayrollColumnVisibility(clientCode: string, prefs: UserPreferences): KeyBooleanPair[] {
    if (!prefs?.ClientPrefs[clientCode] || !prefs?.ClientPrefs[clientCode]?.Payroll) {
      return null;
    }

    return prefs.ClientPrefs[clientCode].Payroll.ColumnVisibility;
  }

  public setPayrollColumnVisibility(clientCode: string, visibility: KeyBooleanPair[]) {
    const callback = (prefs: UserPreferences) => {
      prefs.ClientPrefs[clientCode].Payroll.ColumnVisibility = visibility;
    };
    return this._setUserPrefs(clientCode, callback);
  }

  // US 34503
  public setPayrollEmployeeSortOrder(clientCode: string, sortEmployeeByLastName?: boolean) {
    const callback = (prefs: UserPreferences) => {
      prefs.ClientPrefs[clientCode].Payroll.SortEmployeeByLastName = sortEmployeeByLastName;
    };
    return this._setUserPrefs(clientCode, callback);
  }

  private _setUserPrefs(clientCode: string, callback: any) {
    return this.getUserPreferences().pipe(
      concatMap(prefs => {
        const insert = !prefs;

        if (clientCode) {
          prefs = this.setCompanyKey(clientCode, prefs);
        }

        if (typeof callback === 'function') {
          callback(prefs);
        }

        const p = JSON.stringify(prefs);

        return insert ? this.insertUserPreferences(p) : this.updateUserPreferences(p);
      })
    );
  }

  // #end region
}
